import React, {
  type MutableRefObject,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type * as T from "@src/types";

/** Tracks source of candle data and resolution changes */
export interface CandleSourceInfo {
  /** Original resolution from cache/backtest */
  originalResolution: string | null;
  /** Current resolution (may differ after user changes it) */
  currentResolution: string | null;
  /** True if user changed resolution from original */
  isResolutionChanged: boolean;
}

interface RunResultContextType {
  tradingViewWidgetRef: MutableRefObject<T.TV.IChartingLibraryWidget | null>;
  tradingViewContainerRef: MutableRefObject<HTMLDivElement | null>;
  datafeedRef: MutableRefObject<T.ExternalDatafeed | null>;
  // Where the chart was looking, so a widget rebuilt on a newer cache opens on the same place
  savedViewportRef: MutableRefObject<T.SavedViewport | null>;
  // Run info for telemetry
  run: T.ApiRun | null;
  runType: "backtest" | "execution" | null;
  // Candle source tracking
  candleSource: CandleSourceInfo;
  setCandleSource: (info: Partial<CandleSourceInfo>) => void;
}

const RunResultContext = createContext<RunResultContextType | null>(null);

export const useRunResultContext = () => {
  const context = useContext(RunResultContext);
  if (!context) {
    throw new Error("useRunResultContext must be used within RunResultProvider");
  }
  return context;
};

interface ProviderProps {
  children: React.ReactNode;
  run?: T.ApiRun;
  runType?: "backtest" | "execution";
}

export const RunResultProvider: React.FC<ProviderProps> = ({ children, run, runType }) => {
  const tradingViewWidgetRef = useRef<T.TV.IChartingLibraryWidget | null>(null);
  const tradingViewContainerRef = useRef<HTMLDivElement | null>(null);
  const datafeedRef = useRef<T.ExternalDatafeed | null>(null);
  const savedViewportRef = useRef<T.SavedViewport | null>(null);

  const [candleSource, setCandleSourceState] = useState<CandleSourceInfo>({
    originalResolution: null,
    currentResolution: null,
    isResolutionChanged: false,
  });

  const setCandleSource = useCallback((info: Partial<CandleSourceInfo>) => {
    setCandleSourceState((prev) => {
      const updated = { ...prev, ...info };
      // Auto-compute isResolutionChanged
      updated.isResolutionChanged =
        updated.originalResolution !== null &&
        updated.currentResolution !== null &&
        updated.originalResolution !== updated.currentResolution;
      return updated;
    });
  }, []);

  const value = useMemo(
    () => ({
      tradingViewWidgetRef,
      tradingViewContainerRef,
      datafeedRef,
      savedViewportRef,
      run: run || null,
      runType: runType || null,
      candleSource,
      setCandleSource,
    }),
    [run, runType, candleSource, setCandleSource],
  );

  return <RunResultContext.Provider value={value}>{children}</RunResultContext.Provider>;
};
